import { post } from '@rails/request.js';

import { awaitWithSignal, withRequestDeadline } from './abortSignalAfter';
import { assignDocument, reloadDocument } from './desktop_navigation_diagnostics';
import { getMetaContent, rootDomainUrl, setMetaContent } from './meta';

const defaultHost = () => getMetaContent('env:default_host');
const apiOrigin = () => getMetaContent('env:api_origin');
const clientId = () => getMetaContent('oauth2:client_id');

// `user` is the signed-in browser user's token. On shared-channel preview pages
// the default token is a shadow token, but `oauth2:user_access_token` is also
// minted so writes that require a real user (e.g. join) can authenticate as
// the user without giving up the shadow's read scope.
//
// Browsers hold access tokens only. Refresh tokens are still rendered into the
// document for older cached bundles, but nothing here reads them: every
// document sharing one refresh-token family raced the others into
// `invalid_grant`. The Devise session cookie is the durable credential now, and
// the broker below mints a fresh access token per document on demand.
const TOKEN_KINDS = {
  default: { access: 'oauth2:access_token' },
  user: { access: 'oauth2:user_access_token' },
};

const BROKER_PATH = '/v2/auth/browser_token';

const _refreshPromises = new Map();

let _sessionExpiredNavigation = false;

/** Generate a v2 API route. */
export const apiRoute = (path) => {
  if (path.includes('/v1')) throw new Error('v1 API routes are not supported via this helper');
  if (path.includes('/v2')) throw new Error('do not pass the /v2 prefix to this helper');
  if (path.includes('://')) throw new Error('path must be relative');

  if (!path.startsWith('/')) path = `/${path}`;

  return `${apiOrigin() || `https://api.${defaultHost()}`}/v2${path}`;
};

/** Extract the first validation message from a v2 error body, or a fallback. */
export function responseErrorMessage(data, fallback) {
  const validations = data?.validations || {};
  const firstValidation = Object.values(validations).flat().find(Boolean);
  return firstValidation || fallback;
}

function readToken(kind) {
  const token = getMetaContent(TOKEN_KINDS[kind].access);
  // On signed-out shared-channel pages no user token is minted; fall back to
  // the default (shadow) token so the server can return its own 401.
  if (kind === 'user' && !token) {
    return {
      token: getMetaContent(TOKEN_KINDS.default.access),
      refreshOnUnauthorized: Boolean(getMetaContent('current_user:id')),
    };
  }

  return { token, refreshOnUnauthorized: true };
}

/**
 * Send the user back to the web app once. Use the web app host (web.<base>),
 * not the apex — the apex is a separate marketing site, and redirecting there
 * bounces the desktop app out to the system browser instead of keeping the user
 * in the Rails web app. Guarded so concurrent failures navigate only once.
 */
function navigateToSignedOutDocument() {
  if (_sessionExpiredNavigation) return;
  _sessionExpiredNavigation = true;
  assignDocument(rootDomainUrl('/'), 'auth-session-expired');
}

/**
 * Reload the current document once. Recovery path for shadow documents: the
 * layout re-mints a fresh shadow token on every render of the share page, so
 * reloading in place keeps the viewer on their channel. Shares the one-shot
 * guard with sign-out navigation — one navigation action per document.
 */
function reloadShadowDocument() {
  if (_sessionExpiredNavigation) return;
  _sessionExpiredNavigation = true;
  reloadDocument('auth-session-expired');
}

/**
 * Ask the same-origin broker for a fresh access-only token; updates meta from
 * the response. Returns the new access token, or null when the caller should
 * simply surface the original 401.
 */
async function requestBrokeredAccessToken(kind) {
  let body;
  try {
    body = await withRequestDeadline(async (signal) => {
      // Same-origin request supplies the session cookie and CSRF token.
      const response = await post(BROKER_PATH, { responseKind: 'json', signal });
      if (signal.aborted) return null;
      if (!response.ok) {
        if (response.statusCode === 401 || response.statusCode === 403) navigateToSignedOutDocument();
        return null;
      }
      return response.json;
    }, 15_000);
  } catch {
    // A timeout/network error proves nothing about the browser session.
    return null;
  }

  const accessToken = body?.data?.token?.access_token;
  if (typeof accessToken !== 'string' || !accessToken.trim()) return null;

  setMetaContent(TOKEN_KINDS[kind].access, accessToken);
  return accessToken;
}

/** Fetch a fresh access token, coalescing concurrent calls into one request per kind. */
async function ensureFreshToken(kind) {
  if (!_refreshPromises.has(kind)) {
    const promise = requestBrokeredAccessToken(kind).finally(() => _refreshPromises.delete(kind));
    _refreshPromises.set(kind, promise);
  }
  return _refreshPromises.get(kind);
}

/**
 * fetch() wrapper for v2 API calls. Automatically attaches the Bearer token
 * and retries once with a brokered token on 401.
 */
export async function apiRequest(path, options = {}) {
  return apiRequestWithKind(path, options, 'default');
}

export async function apiRequestAsUser(path, options = {}) {
  return apiRequestWithKind(path, options, 'user');
}

async function apiRequestWithKind(path, options, kind) {
  const url = apiRoute(path);

  const attempt = (token) =>
    fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });

  const { token, refreshOnUnauthorized } = readToken(kind);
  const res = await attempt(token);

  if (res.status !== 401 || !refreshOnUnauthorized) return res;

  // The broker only ever mints tokens for `current_user`, so it cannot renew a
  // shadow identity. On a shadow document the default token is the shadow's:
  // reload in place and let the layout re-mint it, keeping the viewer on their
  // share page. The `user` kind is a real current_user token even on those
  // pages, so it still goes through the broker — that is what keeps the
  // shared-channel join flow working.
  if (kind === 'default' && getMetaContent('current_shadow:id')) {
    reloadShadowDocument();
    return res;
  }

  const fresh = await awaitWithSignal(ensureFreshToken(kind), options.signal);
  // Retry exactly once, and only with a real token — never `Bearer undefined`.
  if (!fresh) return res;

  return attempt(fresh);
}

/** merge json body + optional fetch fields (e.g. signal) for PATCH/POST/DELETE helpers */
function buildRequest(method, json, extra = {}) {
  const { headers: extraHeaders, ...rest } = extra;
  return {
    ...rest,
    method,
    headers: { 'Content-Type': 'application/json', ...(extraHeaders || {}) },
    body: JSON.stringify(json),
  };
}

export async function apiGet(path, extra = {}) {
  return apiRequest(path, {
    ...extra,
    method: 'GET',
  });
}

export async function apiGetAsUser(path, extra = {}) {
  return apiRequestAsUser(path, {
    ...extra,
    method: 'GET',
  });
}

export async function apiPatch(path, json = {}, extra = {}) {
  return apiRequest(path, buildRequest('PATCH', json, extra));
}

export async function apiPatchAsUser(path, json = {}, extra = {}) {
  return apiRequestAsUser(path, buildRequest('PATCH', json, extra));
}

export async function apiPost(path, json = {}, extra = {}) {
  return apiRequest(path, buildRequest('POST', json, extra));
}

export async function apiPostAsUser(path, json = {}, extra = {}) {
  return apiRequestAsUser(path, buildRequest('POST', json, extra));
}

/**
 * POST a multipart/form-data body (FormData). Content-Type is deliberately left unset so
 * the browser can generate the boundary; setting it by hand produces an unparseable body.
 * FormData is replayable, so the 401-retry in apiRequest still works.
 */
export async function apiPostForm(path, formData, extra = {}) {
  const { headers, ...rest } = extra;
  return apiRequest(path, { ...rest, method: 'POST', headers: { ...(headers || {}) }, body: formData });
}

export async function apiDelete(path, json = {}, extra = {}) {
  return apiRequest(path, buildRequest('DELETE', json, extra));
}

export async function apiDeleteAsUser(path, json = {}, extra = {}) {
  return apiRequestAsUser(path, buildRequest('DELETE', json, extra));
}

/** Revoke a token on sign out. */
export async function revokeToken(token) {
  await fetch(apiRoute('/oauth/revoke'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: clientId(), token }),
  });
}

/** Reads recovery JSON with a bounded request/body lifetime; never retries transport failures. */
export async function apiRecoveryRead(path, { asUser = false, json } = {}) {
  return withRequestDeadline(async (signal) => {
    const options = json === undefined ? { method: 'GET', signal } : buildRequest('POST', json, { signal });
    const response = await apiRequestWithKind(path, options, asUser ? 'user' : 'default');
    const data = await response.json();
    return { response, data };
  }, 20_000);
}
